import {
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { IncomeCallService } from 'src/app/_Common/_Services/income-call.service';
import { CubeAgentConfigService } from '../calling/cube/cube-agent-config.service';
import { CubeConnectorCoreService } from '../calling/cube/cube-connector-core.service';
import { CubeDialerFlowService } from '../calling/cube/cube-dialer-flow.service';
import {
  buildCubePopupLoginUrl,
  buildCubeWebphoneLoginUrl,
  cubeConnectorStatusLabel,
  getCubeAgentConfigFromRemarks,
  getCubePopupBaseUrl,
  getCubeWebphoneUrl,
  isCubeAgentConfigComplete,
  isCubeIframeDebugEnabled,
  isCubeIframeVisible,
  isCubePopupConnector,
  isCubeWebphoneConnector,
  CubeConnectorUiStatus,
} from '../calling/cube/cube-dialer-mode';
import { shouldUseCubeSdk, getCubeBridgeIframeId } from '../calling/cube-sdk/cube-sdk-mode';
import { CubeSdkService } from '../calling/cube-sdk/cube-sdk.service';

/**
 * Cube webphone host. SDK mode: iframe id=webphoneFrame + CubeConnector.
 * Debug chrome (isCubeIframeVisible) shares the same CubeSdkService as mt-calling.
 */
@Component({
    selector: 'mt-cube-iframe',
    templateUrl: './cube-iframe.component.html',
    styleUrls: ['./cube-iframe.component.scss'],
    host: {
        '[class.cube-iframe-host--visible]': 'iframeVisible',
        '[class.cube-iframe-host--hidden]': '!iframeVisible',
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CubeIframeComponent implements OnInit, OnDestroy {
  iframeUrl: SafeResourceUrl | null = null;
  isMinimized = false;
  isLoggedIn = false;
  statusText = 'Initializing...';
  isPopupMode = isCubePopupConnector();
  isWebphoneMode = isCubeWebphoneConnector();
  iframeVisible = isCubeIframeVisible();
  cubeFrameId = getCubeBridgeIframeId();
  useCubeSdk = shouldUseCubeSdk();
  cubeFrameAllow = shouldUseCubeSdk() ? 'microphone' : 'microphone *; camera *; autoplay *';
  sdkDebug = shouldUseCubeSdk() && this.iframeVisible;
  agentId = '';
  extension = '';
  password = '';
  campaignId = '';
  phone = '';
  debugLog = '';
  iframeCallControlsActive = true;
  isDragging = false;
  private capturedInteractions: Array<Record<string, unknown>> = [];
  position = { x: 0, y: 0 };
  dragOffset = { x: 0, y: 0 };
  private subs: Subscription[] = [];
  private bootstrapInProgress = false;
  private iframeLoadSeen = false;
  private lastCallStatus = '';
  private onMouseMove = (event: MouseEvent) => {
    if (!this.isDragging) return;
    this.position.x = event.clientX - this.dragOffset.x;
    this.position.y = event.clientY - this.dragOffset.y;
  };
  private onMouseUp = () => {
    this.isDragging = false;
  };

  constructor(
    private sanitizer: DomSanitizer,
    private cubeAgentConfig: CubeAgentConfigService,
    private cubeConnector: CubeConnectorCoreService,
    private cubeDialerFlow: CubeDialerFlowService,
    private incomeCallService: IncomeCallService,
    private cubeSdk: CubeSdkService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    if (!this.isWebphoneMode) {
      this.iframeUrl = this.buildPopupIframeUrl();
    } else if (this.useCubeSdk) {
      this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(getCubeWebphoneUrl());
    }
  }

  ngOnInit(): void {
    this.setupDragging();
    if (this.isWebphoneMode) {
      void this.initWebphoneIframe();
      this.wireConnectorEvents();
      if (this.sdkDebug) {
        this.wireSdkDebugLogs();
      }
    } else {
      this.initPopupIframe();
    }
  }

  onWebphoneFrameLoad(): void {
    this.iframeLoadSeen = true;
    this.debug('IFRAME LOAD', 'Webphone loaded');
    // Widget runs Init → Login → BreakEnd here; debug buttons are a manual retry only.
    void this.ensureConnectorBootstrapped('iframe-load');
  }

  @HostListener('window:pagehide')
  onPageHide(): void {
    if (this.isWebphoneMode) {
      this.cubeConnector.teardownForUnload();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    if (this.isWebphoneMode) {
      this.cubeConnector.teardownForUnload();
    }
  }

  private setStatus(text: string, loggedIn?: boolean): void {
    this.ngZone.run(() => {
      this.statusText = text;
      if (loggedIn !== undefined) {
        this.isLoggedIn = loggedIn;
      }
      this.cdr.markForCheck();
    });
  }

  private buildPopupIframeUrl(): SafeResourceUrl {
    const agent = getCubeAgentConfigFromRemarks();
    if (isCubeAgentConfigComplete(agent)) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(buildCubePopupLoginUrl(agent));
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(getCubePopupBaseUrl());
  }

  private async initWebphoneIframe(): Promise<void> {
    let agent = await this.cubeAgentConfig.ensureLoaded();
    if (!isCubeAgentConfigComplete(agent)) {
      agent = await this.cubeAgentConfig.ensureLoaded(true);
    }
    if (!isCubeAgentConfigComplete(agent)) {
      this.setStatus('Cube credentials missing — log out and log in', false);
      this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(getCubeWebphoneUrl());
      this.cdr.markForCheck();
      return;
    }
    if (shouldUseCubeSdk()) {
      this.fillAgent(agent);
      this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(getCubeWebphoneUrl());
      this.setStatus('Waiting for iframe — then Initialize + Login', false);
      this.cdr.markForCheck();
      return;
    }
    const loginUrl = buildCubeWebphoneLoginUrl(agent);
    console.log('[CubeIframe] Agent credentials ready — webphone SSO', {
      agentId: agent.agentId,
      campaignId: agent.campaignId,
      extension: agent.extension,
      hasPassword: !!agent.password,
    });
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(loginUrl);
    this.cdr.markForCheck();
  }

  private initPopupIframe(): void {
    const agent = getCubeAgentConfigFromRemarks();
    this.setStatus(agent ? 'Loading QuickCall...' : 'Login manually in panel');
  }

  private applyConnectorStatus(status: CubeConnectorUiStatus): void {
    const sessionReady = this.cubeConnector.isCubeSessionReady();
    const loggedIn = this.cubeConnector.isAgentLoggedIn();
    let label = cubeConnectorStatusLabel(status);
    if (this.cubeConnector.isAgentOnBreak()) {
      label =
        String(this.cubeConnector.callState$.getValue()?.breakValue || '').trim() || 'On Break';
    } else if (!sessionReady) {
      if (status === 'ready' || status === 'on_call' || status === 'on_break') {
        label = this.cubeConnector.isLoginInFlight() ? 'Logging in...' : 'Login required';
      } else if (status === 'logging_in' || status === 'loading_webphone' || status === 'initializing') {
        label = 'Logging in...';
      } else if (status === 'failed') {
        label = this.cubeConnector.getLastLoginError() || 'Login failed';
      }
    }
    this.setStatus(label, loggedIn);
  }

  private wireConnectorEvents(): void {
    this.applyConnectorStatus(this.cubeConnector.getCurrentStatus());
    this.iframeCallControlsActive = true;
    this.installInteractionCapture();
    this.subs.push(
      this.cubeConnector.status$.subscribe((status) => {
        this.applyConnectorStatus(status);
      }),
      this.cubeConnector.callState$.subscribe((state) => {
        if (!state) {
          return;
        }
        const status = state.Status || 'Idle';
        if (status !== this.lastCallStatus) {
          this.lastCallStatus = status;
          this.incomeCallService.setCallingRes(
            this.cubeDialerFlow.toCallingState(state, status)
          );
        }
        this.iframeCallControlsActive = true;
        if (state.Status === 'Break' || state.pauseCall) {
          this.applyConnectorStatus('on_break');
        } else if (state.Status === 'CONNECTED') {
          this.setStatus('On call — use iframe or mt-calling', true);
        } else if (state.Status === 'CALLING' && state.customerAnswered) {
          this.setStatus('Customer answered — answer in iframe/widget', true);
        } else if (state.Status === 'CALLING') {
          this.setStatus('Dialing customer...', true);
        }
        this.cdr.markForCheck();
      }),
      this.cubeConnector.iframeCallControlsActive$.subscribe(() => {
        this.iframeCallControlsActive = true;
        this.cdr.markForCheck();
      }),
      this.cubeConnector.callEnded$.subscribe(() => {
        this.applyConnectorStatus(this.cubeConnector.getCurrentStatus());
      }),
      this.cubeConnector.error$.subscribe((msg) => {
        if (!this.cubeConnector.isCubeSessionReady()) {
          this.setStatus(msg, false);
        }
      })
    );
  }

  private installInteractionCapture(): void {
    const debug = isCubeIframeDebugEnabled();
    try {
      localStorage.removeItem('cube_iframe_captured_interactions');
      localStorage.removeItem('cube_iframe_captured_events');
    } catch (_e) {}
    this.capturedInteractions = [];
    const handler = (event: MessageEvent) => {
      const data = event.data as Record<string, unknown> | null;
      if (!data || typeof data !== 'object') {
        return;
      }
      const source = String(data['source'] || '');
      const type = String(data['type'] || '');
      if (debug) {
        if (source !== 'WEBPHONE' && source !== 'child' && source !== 'SF_ADAPTER') {
          return;
        }
        const entry = {
          at: new Date().toISOString(),
          direction: source === 'SF_ADAPTER' ? 'parent→iframe' : 'iframe→parent',
          source,
          type,
          payload: data,
        };
        this.capturedInteractions.push(entry);
        try {
          localStorage.setItem(
            'cube_iframe_captured_events',
            JSON.stringify(this.capturedInteractions.slice(-300))
          );
        } catch (_e) {}
        console.log('[CubeIframeDebug]', entry.direction, type, data);
        return;
      }
      if (source !== 'WEBPHONE' || type !== 'UI_INTERACTION') {
        return;
      }
      if (data['action'] !== 'click') {
        return;
      }
      const entry = {
        at: new Date().toISOString(),
        text: data['text'] || '',
        label: data['label'] || '',
        icon: data['icon'] || '',
        tag: data['tag'] || '',
        className: data['className'] || '',
      };
      this.capturedInteractions.push(entry);
      try {
        localStorage.setItem(
          'cube_iframe_captured_interactions',
          JSON.stringify(this.capturedInteractions.slice(-200))
        );
      } catch (_e) {}
      console.log('[CubeIframeCapture] click', entry);
    };
    window.addEventListener('message', handler);
    this.subs.push(new Subscription(() => window.removeEventListener('message', handler)));
  }

  dumpCapturedInteractions(): void {
    const debug = isCubeIframeDebugEnabled();
    const key = debug ? 'cube_iframe_captured_events' : 'cube_iframe_captured_interactions';
    const stored = localStorage.getItem(key);
    const list = stored ? JSON.parse(stored) : this.capturedInteractions;
    console.group(debug ? '[CubeIframeDebug] all events' : '[CubeIframeCapture] clicks only');
    console.table(
      (list || []).map((row: Record<string, unknown>) => ({
        at: row['at'],
        direction: row['direction'] || 'iframe→parent',
        type: row['type'] || row['text'],
        source: row['source'] || '',
        text: row['text'] || '',
        label: row['label'] || '',
      }))
    );
    console.log(JSON.stringify(list, null, 2));
    console.groupEnd();
    this.setStatus(`Captured ${Array.isArray(list) ? list.length : 0} — see console`, this.isLoggedIn);
  }

  private fillAgent(agent: { agentId: string; extension: string; password: string; campaignId: string } | null): void {
    if (!agent) {
      return;
    }
    this.agentId = agent.agentId;
    this.extension = agent.extension;
    this.password = agent.password;
    this.campaignId = agent.campaignId;
  }

  private debug(event: string, data?: unknown): void {
    let s = '';
    if (data !== undefined) {
      try {
        s = typeof data === 'string' ? data : JSON.stringify(data);
      } catch (_e) {
        s = String(data);
      }
    }
    s = s.replace(/("?(?:password|passwd|pwd)"?\s*[:=]\s*["']?)([^"',}\s]+)/gi, '$1***');
    const line = `${new Date().toLocaleTimeString()} | ${event}${s ? ' | ' + s : ''}`;
    this.debugLog = `${this.debugLog}${line}\n`;
    console.log('[CubeSDK Debug]', event, data);
    this.cdr.markForCheck();
  }

  private wireSdkDebugLogs(): void {
    this.subs.push(
      this.cubeSdk.ready$.subscribe((e) => this.debug('CALLBACK onReady', e)),
      this.cubeSdk.loggedIn$.subscribe((e) => this.debug('CALLBACK onLoggedIn', e)),
      this.cubeSdk.loginFailed$.subscribe((e) => this.debug('CALLBACK onLoginFailed', e)),
      this.cubeSdk.loggedOut$.subscribe((e) => this.debug('CALLBACK onLoggedOut', e)),
      this.cubeSdk.inboundCall$.subscribe((e) => this.debug('CALLBACK onInboundCall', e)),
      this.cubeSdk.callAnswered$.subscribe((e) => this.debug('CALLBACK onCallAnswered', e)),
      this.cubeSdk.callEnded$.subscribe((e) => this.debug('CALLBACK onCallEnded', e)),
      this.cubeSdk.callFailed$.subscribe((e) => this.debug('CALLBACK onCallFailed', e)),
      this.cubeSdk.breakStarted$.subscribe((e) => this.debug('CALLBACK onBreakStarted', e)),
      this.cubeSdk.breakEnded$.subscribe((e) => this.debug('CALLBACK onBreakEnded', e)),
      this.cubeSdk.message$.subscribe((e) => this.debug('CALLBACK onMessage', e)),
      this.cubeSdk.error$.subscribe((e) => this.debug('CALLBACK onError', e)),
      this.cubeSdk.iframeLoad$.subscribe((e) => this.debug('CALLBACK onIframeLoad', e))
    );
  }

  async debugInit(): Promise<void> {
    try {
      if (this.cubeConnector.isInitialized()) {
        this.debug('INIT', 'Already initialized');
        this.setStatus('Connector initialized — click Login', false);
        return;
      }
      await this.cubeConnector.init('webphoneFrame');
      this.debug('INIT', 'CubeConnector created');
      this.setStatus('Connector initialized — click Login', false);
    } catch (e) {
      this.debug('INIT EXCEPTION', e instanceof Error ? e.message : e);
      this.setStatus('Connector init failed', false);
    }
  }

  debugLogin(): void {
    const a = {
      agentId: this.agentId.trim(),
      extension: this.extension.trim(),
      password: this.password,
      campaignId: this.campaignId.trim(),
    };
    const miss = (['agentId', 'extension', 'password', 'campaignId'] as const).filter((k) => !a[k]);
    if (miss.length) {
      this.debug('VALIDATION', 'Missing: ' + miss.join(', '));
      this.setStatus('Missing required fields', false);
      return;
    }
    if (!this.cubeConnector.isInitialized()) {
      this.debug('LOGIN', 'Initialize first');
      this.setStatus('Initialize first', false);
      return;
    }
    this.cubeSdk.setAgent(a);
    this.debug('setAgent()', `agentId=${a.agentId} extension=${a.extension} campaignId=${a.campaignId}`);
    this.cubeSdk.login();
    this.debug('LOGIN', 'Waiting for callback — then Break End');
    this.setStatus('Login sent — then Break End', false);
  }

  debugLogout(): void {
    this.cubeConnector.logout();
  }

  debugCall(): void {
    const n = this.phone.trim();
    if (!n) {
      this.debug('MAKE CALL', 'Enter phone');
      return;
    }
    this.cubeConnector.makeCall(n, { callDirection: 'manual' });
  }

  debugAnswer(): void {
    this.cubeConnector.answer();
  }

  debugHangup(): void {
    this.cubeConnector.hangup();
  }

  debugHold(): void {
    void this.cubeConnector.holdCall();
  }

  debugUnhold(): void {
    void this.cubeConnector.unholdCall();
  }

  debugMute(): void {
    void this.cubeConnector.mute();
  }

  debugUnmute(): void {
    void this.cubeConnector.unmute();
  }

  debugTransfer(): void {
    const number = String(prompt('Enter transfer number:') || '').trim();
    if (!number) {
      return;
    }
    void this.cubeConnector.transfer(number);
  }

  debugRedirect(): void {
    const number = String(prompt('Enter redirect number:') || '').trim();
    if (!number) {
      return;
    }
    this.cubeSdk.redirect(number);
  }

  debugBreakStart(): void {
    void this.cubeConnector.breakStart('Lunch Break');
  }

  debugBreakEnd(): void {
    void this.cubeConnector.breakEnd();
  }

  debugDispose(): void {
    const name = String(prompt('Disposition name:') || '').trim();
    if (!name) {
      return;
    }
    this.cubeConnector.dispose(name, true);
  }

  private async ensureConnectorBootstrapped(source: string): Promise<void> {
    if (this.cubeConnector.isAgentLoggedIn()) {
      this.applyConnectorStatus('ready');
      return;
    }

    if (this.bootstrapInProgress) {
      return;
    }

    this.bootstrapInProgress = true;
    try {
      let agent = await this.cubeAgentConfig.ensureLoaded();
      if (!isCubeAgentConfigComplete(agent)) {
        agent = await this.cubeAgentConfig.ensureLoaded(true);
      }
      if (!isCubeAgentConfigComplete(agent)) {
        this.setStatus('Cube credentials missing — log out and log in', false);
        return;
      }

      if (!this.cubeConnector.isInitialized()) {
        console.log('[CubeIframe] Bootstrap connector', { source, iframeLoadSeen: this.iframeLoadSeen });
        this.setStatus('Initialize → Login → Break End', false);
        await this.cubeConnector.init(this.cubeFrameId);
        this.cubeConnector.requestLoginStatePing();
      } else if (!this.cubeConnector.isAgentLoggedIn()) {
        this.cubeConnector.requestLoginStatePing();
        this.cubeConnector.requestLogin(source === 'retry');
      }
    } catch (error) {
      console.error('[CubeIframe] Webphone init failed', error);
      this.setStatus('Failed to load Cube webphone — tap Retry', false);
    } finally {
      this.bootstrapInProgress = false;
    }
  }

  async retryLogin(): Promise<void> {
    this.setStatus('Retrying login...');
    if (!this.cubeConnector.isInitialized()) {
      await this.ensureConnectorBootstrapped('retry');
      return;
    }
    await this.cubeConnector.forceLogin();
  }

  private handleCallEvent(data: unknown, status: string): void {
    const payload = (data as { payload?: Record<string, unknown> })?.payload || (data as Record<string, unknown>) || {};
    const state = this.cubeDialerFlow.toCallingState(
      {
        ...payload,
        phoneNo: payload['phoneNo'] || payload['phoneNumber'] || payload['number'],
        fullName: payload['fullName'] || payload['name'],
      },
      status
    );
    this.incomeCallService.setCallingRes(state);
    if (status === 'CONNECTED') {
      this.setStatus('On call', true);
    }
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }

  onMouseDown(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    this.isDragging = true;
    this.dragOffset.x = event.clientX - this.position.x;
    this.dragOffset.y = event.clientY - this.position.y;
    event.preventDefault();
  }

  setupDragging(): void {
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  getTransform(): string {
    return `translate(${this.position.x}px, ${this.position.y}px)`;
  }
}
